import html2canvas from "html2canvas";
import i18n from "@/utils/i18n";
import jsPDF from "jspdf";

export interface CertificateData {
  participantName: string;
  educationTitle: string;
  instructorName: string;
  issueDate: string;
  certificateNumber: string;
  description: string;
}

export const DEFAULT_CERTIFICATE_DATA: CertificateData = {
  participantName: "",
  educationTitle: "",
  instructorName: "",
  issueDate: "",
  certificateNumber: "",
  description: "",
};

/**
 * Demo data used only in the create/edit wizard preview. The actual fields get
 * filled at issuance time by the backend (placeholder substitution), so this
 * just shows what the final certificate will *look* like.
 */
export const DEMO_CERTIFICATE_DATA: CertificateData = {
  participantName: "Ad Soyad",
  educationTitle: i18n.t("certificatePdf.educationName"),
  instructorName: i18n.t("certificatePdf.instructorName"),
  issueDate: new Date().toISOString(),
  certificateNumber: "20260421-0001",
  description: "",
};

/**
 * Generates a PDF from an HTML element (certificate preview)
 * @param element - The HTML element to capture
 * @param filename - The filename for the PDF (without extension)
 * @returns Promise<Blob> - The PDF as a Blob
 */
export async function generateCertificatePDF(
  element: HTMLElement,
  _filename = "certificate"
): Promise<Blob> {
  // Capture the element as a canvas with high quality
  const canvas = await html2canvas(element, {
    scale: 2, // Higher scale for better quality
    useCORS: true, // Allow cross-origin images
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  // Get image data from canvas
  const imgData = canvas.toDataURL("image/png", 1.0);

  // Create PDF in landscape A4 format
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Get PDF dimensions
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  // Calculate aspect ratio to fit the certificate properly
  const canvasAspectRatio = canvas.width / canvas.height;
  const pdfAspectRatio = pdfWidth / pdfHeight;

  let imgWidth = pdfWidth;
  let imgHeight = pdfHeight;
  let offsetX = 0;
  let offsetY = 0;

  if (canvasAspectRatio > pdfAspectRatio) {
    // Canvas is wider - fit to width
    imgHeight = pdfWidth / canvasAspectRatio;
    offsetY = (pdfHeight - imgHeight) / 2;
  } else {
    // Canvas is taller - fit to height
    imgWidth = pdfHeight * canvasAspectRatio;
    offsetX = (pdfWidth - imgWidth) / 2;
  }

  // Add the image to PDF
  pdf.addImage(imgData, "PNG", offsetX, offsetY, imgWidth, imgHeight);

  // Return as blob
  return pdf.output("blob");
}

/**
 * Downloads a PDF blob as a file
 * @param blob - The PDF blob
 * @param filename - The filename (with or without .pdf extension)
 */
export function downloadCertificatePDF(blob: Blob, filename: string): void {
  const finalFilename = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = finalFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generates and downloads a certificate PDF in one step
 * @param element - The HTML element to capture
 * @param filename - The filename for the PDF
 */
export async function generateAndDownloadCertificatePDF(
  element: HTMLElement,
  filename = "certificate"
): Promise<void> {
  try {
    const blob = await generateCertificatePDF(element, filename);
    downloadCertificatePDF(blob, filename);
  } catch (error) {
    console.error("Error generating certificate PDF:", error);
    throw error;
  }
}

/**
 * Generates and downloads a certificate PDF from an HTML string.
 * Renders the HTML in a temporary container, captures it with html2canvas + jsPDF, then removes the container.
 * @param htmlContent - Certificate HTML (e.g. parsedHtml from API)
 * @param filename - The filename for the PDF (without .pdf)
 */
export async function generateAndDownloadCertificatePDFFromHtml(
  htmlContent: string,
  filename = "certificate"
): Promise<void> {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.style.width = "842px";
  container.style.background = "#ffffff";
  container.innerHTML = htmlContent;
  document.body.appendChild(container);
  try {
    await new Promise((r) => setTimeout(r, 400));
    await generateAndDownloadCertificatePDF(container, filename);
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Generates a unique certificate number
 * @returns A unique certificate number string
 */
export function generateCertificateNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `CERT-${timestamp}-${random}`;
}

/**
 * Formats a date string for certificate display
 * @param date - Date object or string
 * @returns Formatted date string
 */
export function formatCertificateDate(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
